/**
 * ComponentMapTab — ERP Workspace "Component Map" tab.
 * Maps UAMS screens to their React component name / path.
 * v2: LocalScannerCard moved to ScanTab. Added Code Snippets modal.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Pencil, Check, X, CheckCircle2, AlertTriangle,
  Code2, FileCode, Globe, RefreshCw,
  Download, Copy, ChevronDown,
} from 'lucide-react';
import ERPAdvancedGrid from '../../components/erp/ERPAdvancedGrid';
import { API, authFetch } from '../../lib/api';
import { erpToast } from '../../components/erp/ERPToast';

const PAGE_SIZES = [20, 50, 100, 200];

// ── Code Snippets Modal ───────────────────────────────────────────────────────
function CodeSnippetsModal({ onClose }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState('menu');
  const [copied,  setCopied]  = useState(null);

  useEffect(() => {
    authFetch(`${API}/erp-workspace/admin/code-snippets`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { erpToast.error(e.message); setLoading(false); });
  }, []);

  const code = tab === 'menu' ? data?.menu : data?.barrel;

  const copy = () => {
    navigator.clipboard?.writeText(code || '').then(() => {
      setCopied(tab); setTimeout(() => setCopied(null), 1800);
    });
  };

  const download = () => {
    const fname = tab === 'menu' ? 'menu.config.js' : 'screens_index.js';
    const blob = new Blob([code || ''], { type: 'text/javascript' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url; a.download = fname; a.click();
    URL.revokeObjectURL(url);
  };

  const tabStyle = (id) => ({
    padding: '6px 14px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
    border: 'none', background: 'none', marginBottom: '-1px',
    borderBottom: tab === id ? '2px solid #3b82f6' : '2px solid transparent',
    color: tab === id ? '#3b82f6' : 'var(--text-secondary)',
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--surface,#fff)', borderRadius: 12, width: 720, maxWidth: '95vw',
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(0,0,0,.28)' }}>
        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 8 }}>
          <Code2 size={16} color="#3b82f6" />
          <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>Code Snippets</span>
          {!loading && (
            <span style={{ fontSize: '0.72rem', color: '#64748b',
              background: 'var(--surface-2,#f1f5f9)', padding: '1px 8px', borderRadius: 10 }}>
              {data?.total || 0} mapped screens
            </span>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4 }}>
            <X size={16} />
          </button>
        </div>
        {/* Tabs */}
        <div style={{ padding: '6px 20px 0', borderBottom: '1px solid var(--border)', display: 'flex', gap: 2 }}>
          <button style={tabStyle('menu')}   onClick={() => setTab('menu')}>
            <Code2 size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />menu.config.js
          </button>
          <button style={tabStyle('barrel')} onClick={() => setTab('barrel')}>
            <FileCode size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />screens/index.js
          </button>
        </div>
        {/* Description */}
        <div style={{ padding: '8px 20px 0', fontSize: '0.73rem', color: '#64748b', lineHeight: 1.5 }}>
          {tab === 'menu'
            ? 'Full module → sub-module → screen navigation tree. Drop into your ERP app as the sidebar/router config.'
            : 'Lazy-import registry for all mapped screens. Use React.lazy() to dynamically load any screen by its UAMS screen ID.'}
        </div>
        {/* Code area */}
        <div style={{ flex: 1, overflow: 'auto', padding: '10px 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
              <RefreshCw size={16} className="spin" style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Generating…
            </div>
          ) : (
            <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.71rem', lineHeight: 1.6,
              color: 'var(--text-primary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              background: 'var(--surface-2,#f8fafc)', padding: '12px 14px', borderRadius: 8,
              border: '1px solid var(--border)', overflow: 'auto', maxHeight: '42vh' }}>
              {code || '// No mapped screens yet.'}
            </pre>
          )}
        </div>
        {/* Footer */}
        <div style={{ padding: '10px 20px', borderTop: '1px solid var(--border)',
          display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="erp-btn" onClick={copy}
            data-testid="compmap-snippets-copy-btn"
            style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}>
            {copied === tab ? <><Check size={12} color="#22c55e" /> Copied!</> : <><Copy size={12} /> Copy</>}
          </button>
          <button className="erp-btn erp-btn-primary" onClick={download}
            data-testid="compmap-snippets-download-btn"
            style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Download size={12} />
            Download {tab === 'menu' ? 'menu.config.js' : 'screens_index.js'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Scan redirect banner ─────────────────────────────────────────────────────
function ScannerBanner({ onNavigateScan }) {
  return (
    <div style={{ borderBottom: '1px solid var(--border)', flexShrink: 0,
      padding: '6px 14px', background: 'rgba(59,130,246,.03)',
      display: 'flex', alignItems: 'center', gap: 8 }}>
      <Code2 size={12} color="#3b82f6" />
      <span style={{ fontSize: '0.73rem', color: '#64748b' }}>
        Have an ERP project? Use the
      </span>
      <button onClick={onNavigateScan}
        style={{ fontSize: '0.73rem', fontWeight: 700, color: '#3b82f6',
          background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
        Scan (Local) tab
      </button>
      <span style={{ fontSize: '0.73rem', color: '#64748b' }}>
        to download the scanner script, run it on your ERP codebase, and bulk-import component mappings here.
      </span>
    </div>
  );
}

// ── Inline editor cell ─────────────────────────────────────────────────────
function EditableCell({ value, onSave, placeholder = '—', mono = false, width = 180 }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  const inputRef = useRef();

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = () => { if (val !== (value || '')) onSave(val); setEditing(false); };

  if (editing) return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      <input ref={inputRef} value={val} onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        style={{ width, fontSize: '0.73rem', padding: '2px 6px', border: '1px solid #3b82f6', borderRadius: 4, fontFamily: mono ? 'monospace' : 'inherit' }} />
      <button onClick={commit} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#22c55e', padding: 0 }}><Check size={12} /></button>
      <button onClick={() => setEditing(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 0 }}><X size={12} /></button>
    </span>
  );

  return (
    <span onClick={() => { setVal(value || ''); setEditing(true); }}
      style={{ cursor: 'text', display: 'inline-flex', alignItems: 'center', gap: 3, color: value ? 'inherit' : '#94a3b8', fontFamily: mono ? 'monospace' : 'inherit', fontSize: '0.73rem' }}>
      {value || placeholder}
      <Pencil size={9} color="#cbd5e1" />
    </span>
  );
}

// ── ContextMenu action dropdown (used via cellRenderer, not ERPActionColumn) ─
function ActionMenu({ row, onEdit, onToggleImpl, onSetCountry, onClear, testId }) {
  const [open, setOpen] = useState(false);
  const [pos,  setPos]  = useState({ top: 0, left: 0 });
  const btnRef = useRef();
  const menuRef = useRef();

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target) && !btnRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: Math.max(4, r.right - 180) });
    }
    setOpen(o => !o);
  };

  const act = (fn) => { setOpen(false); fn(); };

  return (
    <>
      <button ref={btnRef} onClick={toggle}
        data-testid={testId}
        style={{ background: 'none', border: '1px solid var(--border,#e2e8f0)', borderRadius: 5, padding: '3px 7px', cursor: 'pointer', fontSize: '0.68rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 3 }}>
        ⋮ Actions
      </button>
      {open && createPortal(
        <div ref={menuRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999,
            background: 'var(--surface,#fff)', border: '1px solid var(--border,#e2e8f0)',
            borderRadius: 8, boxShadow: '0 8px 28px rgba(0,0,0,.14)', minWidth: 178,
            overflow: 'hidden', padding: '4px 0' }}>
          <div style={{ padding: '4px 12px 6px', fontSize: '0.67rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em', borderBottom: '1px solid var(--border)' }}>
            {row.ScreenId} · {(row.ScreenTitle || '').slice(0,22)}
          </div>
          <MenuItem icon={<Pencil size={12} />}   label="Edit Component Name" color="#3b82f6" onClick={() => act(() => onEdit(row, 'name'))} />
          <MenuItem icon={<FileCode size={12} />}  label="Edit Component Path" color="#8b5cf6" onClick={() => act(() => onEdit(row, 'path'))} />
          <MenuItem icon={<Globe size={12} />}     label="Set Country Code"    color="#06b6d4" onClick={() => act(() => onSetCountry(row))} />
          <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
          <MenuItem icon={row.IsImplemented ? <X size={12} /> : <CheckCircle2 size={12} />}
            label={row.IsImplemented ? 'Mark Not Implemented' : 'Mark Implemented'}
            color={row.IsImplemented ? '#f59e0b' : '#22c55e'}
            onClick={() => act(() => onToggleImpl(row))} />
          <MenuItem icon={<X size={12} />} label="Clear Mapping" color="#ef4444" onClick={() => act(() => onClear(row))} />
        </div>,
        document.body
      )}
    </>
  );
}

function MenuItem({ icon, label, color, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      onClick={onClick}
      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', background: hover ? 'var(--surface-2,#f8fafc)' : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '0.75rem', color: hover ? color : 'var(--text-primary)' }}>
      <span style={{ color, flexShrink: 0 }}>{icon}</span>
      {label}
    </button>
  );
}

// ── Inline edit modal ──────────────────────────────────────────────────────
function EditModal({ row, field, onSave, onClose }) {
  const [val, setVal] = useState(row[field === 'name' ? 'ComponentName' : field === 'path' ? 'ComponentPath' : 'CountryCode'] || '');
  const labels = { name: 'Component Name', path: 'Component Path', country: 'Country Code (e.g. IN, US) — leave blank for ALL' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--surface,#fff)', borderRadius: 10, padding: 24, width: 400, boxShadow: '0 12px 40px rgba(0,0,0,.18)' }}>
        <div style={{ fontWeight: 700, marginBottom: 12, fontSize: '0.9rem' }}>
          Edit — {row.ScreenId} · {row.ScreenTitle}
        </div>
        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
          {labels[field]}
        </label>
        <input autoFocus value={val} onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') onSave(val); if (e.key === 'Escape') onClose(); }}
          placeholder={field === 'country' ? 'IN / US / ALL / blank' : field === 'name' ? 'MyComponent' : 'pages/module/MyComponent.jsx'}
          style={{ width: '100%', fontSize: '0.78rem', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontFamily: field !== 'country' ? 'monospace' : 'inherit' }} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} className="erp-btn" style={{ fontSize: '0.75rem' }}>Cancel</button>
          <button onClick={() => onSave(val)} className="erp-btn erp-btn-primary" style={{ fontSize: '0.75rem' }}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export function ComponentMapTab({ modules, unmatchedHighlight, onClearUnmatched, onNavigateScan, flashIds, onClearFlash }) {
  const [rows,         setRows]         = useState([]);
  const [total,        setTotal]        = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [page,         setPage]         = useState(1);
  const [pageSize,     setPageSize]     = useState(50);
  const [sortField,    setSortField]    = useState('ScreenId');
  const [sortDir,      setSortDir]      = useState('asc');
  const [filters,      setFilters]      = useState({});
  const [editTarget,   setEditTarget]   = useState(null);
  const [saving,       setSaving]       = useState(new Set());
  const [showSnippets, setShowSnippets] = useState(false);

  // ── Auto-clear flash highlights after 5 seconds ─────────────────────────
  useEffect(() => {
    if (!flashIds?.size) return;
    const t = setTimeout(() => onClearFlash?.(), 5000);
    return () => clearTimeout(t);
  }, [flashIds, onClearFlash]);

  useEffect(() => { if (unmatchedHighlight?.length) setFilters(f => ({ ...f, IsImplemented: '0' })); }, [unmatchedHighlight]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ industryCode: 'SHARED', page, pageSize, sortField, sortDir });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const r = await authFetch(`${API}/erp-workspace/admin/component-map?${params}`);
      const d = await r.json();
      if (Array.isArray(d)) { setRows(d); setTotal(d.length); }
      else                  { setRows(d.rows || d); setTotal(d.total || (d.rows || d).length); }
    } catch (e) { erpToast.error(e.message); }
    setLoading(false);
  }, [page, pageSize, sortField, sortDir, filters]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (screenId, patch) => {
    setSaving(s => new Set(s).add(screenId));
    try {
      const row = rows.find(r => r.ScreenId === screenId);
      const r = await authFetch(`${API}/erp-workspace/admin/component-map/${screenId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ componentName: row?.ComponentName, componentPath: row?.ComponentPath, isImplemented: row?.IsImplemented, countryCode: row?.CountryCode, ...patch }),
      });
      const d = await r.json();
      if (d.ok) { setRows(rs => rs.map(r => r.ScreenId === screenId ? { ...r, ...patch } : r)); erpToast.success('Saved'); }
      else       { erpToast.error(d.error); }
    } catch (e) { erpToast.error(e.message); }
    setSaving(s => { const ns = new Set(s); ns.delete(screenId); return ns; });
  }, [rows]);

  const toggleImpl  = (row) => save(row.ScreenId, { isImplemented: !row.IsImplemented,  IsImplemented: !row.IsImplemented });
  const clearMap    = (row) => save(row.ScreenId, { componentName: null, ComponentName: null, componentPath: null, ComponentPath: null, isImplemented: false, IsImplemented: false });
  const openEdit    = (row, field) => setEditTarget({ row, field });
  const openCountry = (row) => setEditTarget({ row, field: 'country' });

  const handleEditSave = async (val) => {
    if (!editTarget) return;
    const { row, field } = editTarget;
    const patch = field === 'name'    ? { componentName: val,  ComponentName: val  }
                : field === 'path'    ? { componentPath: val,  ComponentPath: val  }
                : field === 'country' ? { countryCode: val || null, CountryCode: val || null }
                : {};

    // Country uses different endpoint
    if (field === 'country') {
      setSaving(s => new Set(s).add(row.ScreenId));
      try {
        await authFetch(`${API}/erp-workspace/admin/screen-country/${row.ScreenId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ countryCode: val || null }),
        });
        setRows(rs => rs.map(r => r.ScreenId === row.ScreenId ? { ...r, CountryCode: val || null } : r));
        erpToast.success(`Country: ${val || 'ALL'}`);
      } catch (e) { erpToast.error(e.message); }
      setSaving(s => { const ns = new Set(s); ns.delete(row.ScreenId); return ns; });
    } else {
      await save(row.ScreenId, patch);
    }
    setEditTarget(null);
  };

  const handleSort = (field) => {
    setSortDir(sortField === field && sortDir === 'asc' ? 'desc' : 'asc');
    setSortField(field);
    setPage(1);
  };

  // ── ERPAdvancedGrid columns ─────────────────────────────────────────────
  const COLUMNS = [
    { field: 'ScreenId',       header: 'Screen ID',    sortKey: 'ScreenId',    filterKey: 'screenId',    visible: true,  frozen: true,  width: 88 },
    { field: 'ScreenTitle',    header: 'Title',        sortKey: 'ScreenTitle', filterKey: 'title',       visible: true,  frozen: false, width: 180 },
    { field: 'ScreenType',     header: 'Type',         sortKey: 'ScreenType',  filterKey: 'screenType',  visible: true,  frozen: false, width: 100 },
    { field: 'ModuleId',       header: 'Module',       sortKey: 'ModuleId',    filterKey: 'moduleId',    visible: true,  frozen: false, width: 72 },
    { field: 'ComponentName',  header: 'Component',    sortKey: 'ComponentName',filterKey:'componentName',visible: true,  frozen: false, width: 170 },
    { field: 'ComponentPath',  header: 'Path',         sortKey: 'ComponentPath',filterKey:'componentPath',visible: true,  frozen: false, width: 220 },
    { field: 'IsImplemented',  header: 'Done',         sortKey: 'IsImplemented',filterKey:'implemented',  visible: true,  frozen: false, width: 70 },
    { field: 'CountryCode',    header: 'Country',      sortKey: 'CountryCode', filterKey: 'countryCode', visible: true,  frozen: false, width: 78 },
  ];

  const implCount = rows.filter(r => r.IsImplemented).length;

  const cellRenderer = (field, value, row) => {
    const isFlashed = flashIds?.has(String(row.ScreenId));
    if (field === 'ScreenId') return (
      <span style={{ display:'inline-flex', alignItems:'center', gap:5 }}>
        <code style={{ fontSize:'0.71rem', color: isFlashed ? '#15803d' : '#64748b',
          fontWeight: isFlashed ? 800 : 400 }}>{value}</code>
        {isFlashed && (
          <span style={{ fontSize:'0.6rem', fontWeight:800, color:'#fff',
            background:'#22c55e', padding:'1px 5px', borderRadius:4,
            animation:'none', lineHeight:1.4 }}>
            NEW
          </span>
        )}
      </span>
    );
    if (field === 'ComponentName') return saving.has(row.ScreenId)
      ? <span style={{ color: '#94a3b8', fontSize: '0.73rem' }}>{value || '—'}</span>
      : <EditableCell value={value} placeholder="ComponentName" mono onSave={v => save(row.ScreenId, { componentName: v, ComponentName: v })} />;
    if (field === 'ComponentPath') return saving.has(row.ScreenId)
      ? <span style={{ color: '#94a3b8', fontSize: '0.73rem' }}>{value || '—'}</span>
      : <EditableCell value={value} placeholder="pages/..." mono width={200} onSave={v => save(row.ScreenId, { componentPath: v, ComponentPath: v })} />;
    if (field === 'IsImplemented') return value
      ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.7rem', color: '#22c55e', fontWeight: 700 }}><CheckCircle2 size={12} /> Yes</span>
      : <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>—</span>;
    if (field === 'ScreenType')    return value ? <span style={{ fontSize: '0.68rem', padding: '1px 6px', borderRadius: 4, background: 'var(--surface-3,#f1f5f9)', color: '#475569' }}>{value}</span> : '—';
    if (field === 'CountryCode')   return value ? <span style={{ fontSize: '0.68rem', padding: '1px 6px', borderRadius: 4, background: 'rgba(6,182,212,.1)', color: '#0891b2', fontWeight: 700 }}>{value}</span> : <span style={{ color: '#94a3b8', fontSize: '0.68rem' }}>ALL</span>;
    return value || '—';
  };

  const headerActions = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {flashIds?.size > 0 && (
        <span style={{ fontSize:'0.72rem', fontWeight:700, color:'#15803d',
          background:'rgba(34,197,94,.1)', padding:'2px 8px', borderRadius:4,
          display:'flex', alignItems:'center', gap:4 }}>
          <CheckCircle2 size={11} color="#22c55e" />
          {flashIds.size} newly mapped — highlighted below
          <button onClick={() => onClearFlash?.()}
            style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', padding:0, marginLeft:2 }}>
            <X size={10} />
          </button>
        </span>
      )}
      <span style={{ fontSize: '0.72rem', color: '#22c55e', fontWeight: 700, background: 'rgba(34,197,94,.08)', padding: '2px 8px', borderRadius: 4 }}>
        <CheckCircle2 size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />{implCount} implemented
      </span>
      {unmatchedHighlight?.length > 0 && (
        <span style={{ fontSize: '0.72rem', color: '#f59e0b', fontWeight: 700, background: 'rgba(245,158,11,.08)', padding: '2px 8px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
          <AlertTriangle size={11} />{unmatchedHighlight.length} unmatched — filter applied
          <button onClick={() => { onClearUnmatched?.(); setFilters({}); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, marginLeft: 2 }}><X size={10} /></button>
        </span>
      )}
      <button className="erp-btn" onClick={() => setShowSnippets(true)}
        data-testid="compmap-code-snippets-btn"
        style={{ fontSize: '0.73rem', display: 'flex', alignItems: 'center', gap: 3 }}>
        <Code2 size={11} /> Code Snippets
      </button>
      <button className="erp-btn" onClick={load} style={{ fontSize: '0.73rem', display: 'flex', alignItems: 'center', gap: 3 }}>
        <RefreshCw size={11} className={loading ? 'spin' : ''} /> Refresh
      </button>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }} data-testid="compmap-grid-wrapper">
      <ScannerBanner onNavigateScan={onNavigateScan} />
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <ERPAdvancedGrid
        title={`Component Map — ERP Screen Catalog`}
        columns={COLUMNS}
        data={rows}
        total={total}
        idField="ScreenId"
        nameField="ScreenTitle"
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSort={handleSort}
        filters={filters}
        onFilterChange={(f) => { setFilters(f); setPage(1); }}
        loading={loading}
        showAddNew={false}
        showExport
        exportFileName="erp-component-map"
        testId="compmap-grid"
        pageSizeOptions={PAGE_SIZES}
        headerActions={headerActions}
        cellRenderer={cellRenderer}
        onEdit={(row, mode) => openEdit(row, 'name')}
        onVoid={(row) => clearMap(row)}
      />

      {editTarget && (
        <EditModal
          row={editTarget.row}
          field={editTarget.field}
          onSave={handleEditSave}
          onClose={() => setEditTarget(null)}
        />
      )}
      </div>
      {showSnippets && <CodeSnippetsModal onClose={() => setShowSnippets(false)} />}
    </div>
  );
}
